<template>
  <IndicatorCard
    :loading="loading"
    :has-data="hasData"
    :is-result="isResult"
    :no-data-message="$t('Nenhuma causa encontrada')"
    :title="title"
    :description="indicator.description"
    :excel-endpoint="indicator.excelEndpoint"
    :hide-excel="!isResult"
    :excel-loading="excelLoading"
    @export-excel="requestExcel"
  >
    <template #date-range>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :disabled="isResult"
        @change="onDateRangeChange"
      />
    </template>

    <v-row align="center">
      <v-col cols="12" sm="6">
        <BaseEChart :option="pieOption" :loading="loading" height="320px" />
      </v-col>
      <v-col cols="12" sm="6">
        <v-simple-table class="main-table" dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">{{ $t('Causa') }}</th>
                <th class="text-center">{{ $t('Qtd. O.S.') }}</th>
                <th class="text-center">{{ $t('% do Total') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ $t('Erro Operacional') }}</td>
                <td class="text-center">{{ operationalError }}</td>
                <td class="text-center">{{ pct(operationalError) }}</td>
              </tr>
              <tr>
                <td>{{ $t('Abuso na utilização') }}</td>
                <td class="text-center">{{ abuseInUse }}</td>
                <td class="text-center">{{ pct(abuseInUse) }}</td>
              </tr>
              <tr>
                <td>{{ $t('Dano Acidental') }}</td>
                <td class="text-center">{{ accidentalDamage }}</td>
                <td class="text-center">{{ pct(accidentalDamage) }}</td>
              </tr>
              <tr class="font-weight-bold">
                <td>{{ $t('Total Uso Inadequado') }}</td>
                <td class="text-center">{{ totalUserCause }}</td>
                <td class="text-center">{{ pct(totalUserCause) }}</td>
              </tr>
              <tr>
                <td>{{ $t('Total O.S. Concluídas') }}</td>
                <td class="text-center">{{ totalCompleted }}</td>
                <td class="text-center">100%</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
    </v-row>

    <template #review>
      <v-row class="ma-0">
        <v-col cols="12" v-if="isResult && review">
          <strong>{{ $t('Análise crítica') }}</strong>
          <p v-html="review"></p>
        </v-col>
        <v-col cols="12" v-else-if="!isResult">
          <tiptap-vuetify
            v-model="filter.review"
            :extensions="extensions"
            :label="$t('Análise crítica')"
            :placeholder="$t('Digite sua análise crítica para o indicador abaixo')"
            :card-props="{ flat: true, outlined: true, rounded: true }"
            class="primary-input"
            outlined
            dense
            hide-details="auto"
          ></tiptap-vuetify>
        </v-col>
      </v-row>
    </template>
  </IndicatorCard>
</template>

<script>
import { getIndicatorById } from '../../../../../indicators/indicatorRegistry'
import ApiService from '../../../../../../../../services/ApiService'
import IndicatorCard from '../../shared/IndicatorCard.vue'
import BaseEChart from '../../shared/BaseEChart.vue'
import DateRangePicker from '../../shared/DateRangePicker.vue'
import { buildPieOption } from '../../shared/chartConfigs'
import moment from 'moment'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify'

export default {
  name: 'InappropriateUseEquipment',

  components: {
    IndicatorCard,
    BaseEChart,
    DateRangePicker,
    TiptapVuetify,
  },

  props: {
    title: { type: String, default: '' },
    apiEndpointOverride: { type: String, default: '' },
    snapshotData: { type: Object, default: null },
    filterProp: Object,
    institution: Object,
    unidades: Array,
    isResult: { type: Boolean, default: false },
    review: String,
  },

  data() {
    return {
      excelLoading: false,
      loading: true,
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [Heading, { options: { levels: [1, 2, 3] } }],
        Bold,
        Code,
        HorizontalRule,
        Paragraph,
        HardBreak,
      ],
      filter: { review: '' },
      apiService: new ApiService(),
      startDate: '',
      endDate: '',
      operationalError: 0,
      abuseInUse: 0,
      accidentalDamage: 0,
      totalUserCause: 0,
      totalCompleted: 0,
    }
  },

  computed: {
    indicator() {
      return getIndicatorById('user_cause_defect') || {}
    },

    hasData() {
      return this.totalCompleted > 0
    },

    pieOption() {
      const total = this.totalCompleted
      const option = buildPieOption({
        data: [
          {
            name: this.$t("Outros tipos de O.S.'s"),
            value: this.totalCompleted,
            color: '#0F3565',
          },
          {
            name: this.$t('Total Uso Inadequado'),
            value: this.totalUserCause,
            color: '#ED8990',
          },
        ],
        showLegend: true,
      })
      const inadequatePct = total ? parseFloat(((this.totalUserCause / total) * 100).toFixed(1)) : 0
      const pctFor = (value, dataIndex) => {
        if (!total) return '0.0'
        return dataIndex === 0
          ? (100 - inadequatePct).toFixed(1)
          : ((value / total) * 100).toFixed(1)
      }
      option.series[0].label.formatter = ({ value, dataIndex }) => {
        const pct = pctFor(value, dataIndex)
        return parseFloat(pct) >= 4 ? `${pct}%` : ''
      }
      option.tooltip.formatter = ({ name, value, dataIndex }) => {
        const pct = pctFor(value, dataIndex)
        return `<b>${name}</b><br/>${value} (${pct}%)`
      }
      return option
    },
  },

  created() {
    const lastMonthStart = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')
    const lastMonthEnd = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    this.filter.institutionId = this.institution.id
    this.filter.unidadeIds = this.unidades
    this.filter.startDate = lastMonthStart
    this.filter.endDate = lastMonthEnd
    this.startDate = lastMonthStart
    this.endDate = lastMonthEnd

    if (this.filterProp) {
      this.filter = this.filterProp
      this.startDate = moment(this.filter.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.endDate = moment(this.filter.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    }

    this.list()
  },

  methods: {
    requestExcel() {
      if (!this.indicator.excelEndpoint) return
      this.excelLoading = true
      const filename = `${this.indicator.legacyKey}.xlsx`
      this.apiService
        .post(this.indicator.excelEndpoint, { ...this.filter, pageSize: 999999 })
        .then((response) => {
          const data = `data:application/octet-stream;base64,${response.content}`
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },

    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      this.filterProp.data = null
      this.list()
    },

    setInfos() {
      const model = {
        chart: getIndicatorById('user_cause_defect').legacyKey,
        ...this.filter,
        data: null,
      }
      this.$emit('setInfos', model)
    },

    pct(value) {
      if (!this.totalCompleted) return '0%'
      return `${((value / this.totalCompleted) * 100).toFixed(1)}%`
    },

    extractChartData(data) {
      this.operationalError = data.operationalError || 0
      this.abuseInUse = data.abuseInUse || 0
      this.accidentalDamage = data.accidentalDamage || 0
      this.totalUserCause = data.totalUserCause || 0
      this.totalCompleted = data.totalCompleted || 0
    },

    async list() {
      this.loading = true
      try {
        if (this.filterProp.data) {
          this.extractChartData(JSON.parse(this.filterProp.data))
          return
        }

        if (!this.isResult) this.setDates()
        this.filter.institutionId = this.institution.id
        this.filter.unidadeIds = this.unidades

        const response = await this.apiService.post(
          this.apiEndpointOverride || this.indicator.apiEndpoint,
          this.filter
        )
        this.extractChartData(response.content)
      } finally {
        this.loading = false
      }
    },
  },

  watch: {
    filter: {
      deep: true,
      handler() {
        this.setInfos()
      },
    },
  },
}
</script>
